import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { DetailPanel } from '../primitives/DetailPanel.js';
import { StatusBadge } from '../primitives/Badge.js';
import { postcodeMapLink } from '@cls/shared';

interface ProjectFull {
  id: string; code: string; name: string; customer: string; address: string; postcode: string; status: string;
  startDate: string | null; endDate: string | null; manager: string | null; managerPhone: string | null;
  documents: Array<{ id: string; name: string; type: string; ref: string | null; date: string | null }>;
  supervisor: { name: string; phone: string; email: string } | null;
  clientPM: { name: string; phone: string; email: string } | null;
  clientFM: { name: string; phone: string; email: string } | null;
  clientCM: { name: string; phone: string; email: string } | null;
  clsMgr: { name: string; phone: string; email: string } | null;
}

export function ProjectDetail({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const { data: p } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<ProjectFull>(`/projects/${projectId}`).then((r) => r.data),
  });

  const { data: workers = [] } = useQuery({
    queryKey: ['project-workers', projectId],
    queryFn: () => api.get<Array<{ id: string; name: string; role: string }>>(`/projects/${projectId}/workers`).then((r) => r.data),
  });

  if (!p) return <DetailPanel open title="Loading…" onClose={onClose} />;

  return (
    <DetailPanel open title={`${p.code} — ${p.name}`} subtitle={p.customer} onClose={onClose}>
      <div className="dp-row"><div className="dp-key">Status</div><div className="dp-val"><StatusBadge status={p.status} /></div></div>
      <div className="dp-row"><div className="dp-key">Start</div><div className="dp-val">{p.startDate?.slice(0, 10) ?? '—'}</div></div>
      <div className="dp-row"><div className="dp-key">End</div><div className="dp-val">{p.endDate?.slice(0, 10) ?? '—'}</div></div>
      <div className="dp-row"><div className="dp-key">Address</div><div className="dp-val">{p.address}<br /><a href={postcodeMapLink(p.postcode)} target="_blank" rel="noreferrer">{p.postcode}</a></div></div>
      <div className="divider" />
      <div className="sec-title">Key Contacts</div>
      <div className="fg2" style={{ fontSize: 11 }}>
        {(['clientPM', 'clientFM', 'clientCM', 'clsMgr'] as const).map((k) => {
          const c = p[k];
          const label = { clientPM: 'Client PM', clientFM: 'Client FM', clientCM: 'Client CM', clsMgr: 'CLS Mgr' }[k];
          return (
            <div key={k} className="card-sm">
              <div className="form-label" style={{ margin: 0 }}>{label}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c?.name ?? '—'}</div>
              {c?.phone ? <div style={{ fontSize: 10, color: 'var(--faint)' }}>{c.phone}</div> : null}
            </div>
          );
        })}
      </div>
      <div className="divider" />
      <div className="sec-title">Site Supervisor</div>
      <div style={{ fontSize: 12 }}>{p.supervisor?.name ?? 'Not set'}</div>
      {p.supervisor ? <div style={{ fontSize: 10, color: 'var(--muted)' }}>{p.supervisor.phone} · {p.supervisor.email}</div> : null}
      <div className="divider" />
      <div className="sec-title">Assigned Workers ({workers.length})</div>
      {workers.length ? workers.map((w) => (
        <div key={w.id} style={{ padding: '4px 0', borderBottom: '1px solid rgba(30,42,64,.4)', fontSize: 12 }}>
          {w.name} <span style={{ color: 'var(--muted)', fontSize: 10 }}>· {w.role}</span>
        </div>
      )) : <div style={{ fontSize: 12, color: 'var(--muted)' }}>None yet.</div>}
      <div className="divider" />
      <div className="sec-title">Documents ({p.documents.length})</div>
      {p.documents.length ? p.documents.map((d) => (
        <div key={d.id} className="doc-item">
          <div className="doc-icon">📄</div>
          <div>
            <div style={{ fontWeight: 600 }}>{d.name}</div>
            <div style={{ fontSize: 10, color: 'var(--muted)' }}>{d.type} · {d.date?.slice(0, 10) ?? ''}</div>
          </div>
        </div>
      )) : <div style={{ fontSize: 12, color: 'var(--muted)' }}>No documents.</div>}
    </DetailPanel>
  );
}
